import { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { getRequest as get } from 'api/utils/HttpMethods';

import {
  createEntity,
  useGetQuery,
  useListQuery,
  useMutationQuery,
} from '../utils/queries';
import { isReceipt, receiptSchema } from '@billing/validation/receipt';
import z from 'zod';
import queryString from 'api/utils/queryStringBuilder';

export const receiptQueryKeys = {
  all: ['receipts'],
};

const generateReceipt = async (
  receipt: z.infer<typeof receiptSchema>
): Promise<z.infer<typeof receiptSchema>> =>
  createEntity<z.infer<typeof receiptSchema>>({
    path: '/resource/receipt',
    functionName: 'generateReceipt',
    entity: receipt,
    isEntity: isReceipt,
  });

export const useGenerateReceipt = (): UseMutationResult<
  z.infer<typeof receiptSchema>,
  Error
> => {
  return useMutationQuery<z.infer<typeof receiptSchema>>({
    mutationFn: generateReceipt,
    successMessage: 'Receipt generated successfully.',
    errorMessage: 'Failed to generate receipt.',
    queryKey: receiptQueryKeys.all,
  });
};

const getReceiptByInvoice = async (
  invoiceID: string
): Promise<z.infer<typeof receiptSchema>> =>
  await get<z.infer<typeof receiptSchema>>(
    'QBilling',
    `/resource/receipt${queryString({ invoiceID })}`
  );

export const useGetReceiptByInvoice = (
  invoiceID: string
): UseQueryResult<z.infer<typeof receiptSchema>, Error> => {
  return useGetQuery<z.infer<typeof receiptSchema>>({
    queryKey: ['receipt', invoiceID],
    fetchFunction: getReceiptByInvoice,
    functionName: 'getReceipt',
    isEntity: isReceipt,
    enabled: !!invoiceID,
    params: invoiceID,
  });
};

/**
 * @description Hook to fetch the list of receipts from the server.
 * @returns {UseQueryResult<Receipt[], Error>} The result of the query.
 */
export const useListReceipts = (): UseQueryResult<
  z.infer<typeof receiptSchema>[],
  Error
> => {
  const listReceipts = async (): Promise<z.infer<typeof receiptSchema>[]> =>
    await get<z.infer<typeof receiptSchema>[]>(
      'QBilling',
      `/resource/receipt/all`
    );

  return useListQuery<z.infer<typeof receiptSchema>>({
    queryKey: receiptQueryKeys.all,
    fetchFunction: listReceipts,
    functionName: 'listReceipts',
    isEntity: (obj: any): obj is z.infer<typeof receiptSchema> => {
      const parseResult = receiptSchema.safeParse(obj);
      return parseResult.success;
    },
    enabled: true,
    displayNotification: true,
  });
};
